import { calculateCalibration } from "./calibration";

/**
 * Measures the voice's rate during delivery, instead of before it.
 *
 * Ruling 64 of 12 October 2026: **calibration is not a step.** The trainee
 * never calibrates before a run. He opened the app in Firefox, pressed
 * Calibrate, got nothing, and could not start — and the measurement was only
 * ever a means to doc 03 section 2.1's rates, not an end in itself.
 *
 * So delivery begins at rate 1 and times itself. Once 60 words have been
 * timed — doc 04 section 8.4's figure, and the same 60 the fixed passage
 * used — the multiplier is computed and applied from the next item. Until
 * then the screen says the rate is not yet measured and shows no figure,
 * because a wpm number nobody measured is worse than no number.
 */

/** Doc 04 section 8.4: the words needed before a rate can be trusted. */
export const MEASUREMENT_WORDS = 60;

/**
 * Where a chunk's elapsed time came from.
 *
 * Ruling 64d. The engine's own `start` event is the better mark, because it
 * excludes the time the engine spent getting ready. Firefox withholds it for
 * some voices, so the fallback is the moment `speak` was called, which
 * includes that preparation and so reads slightly slow. Which was used is
 * logged, because a rate measured the second way is a weaker figure.
 */
export type TimingSource = "engine-start" | "speak-call";

export interface LiveRateState {
  /** The measured baseline, or null until there is one. */
  readonly baselineWpm: number | null;
  /** True once 60 words have been timed. */
  readonly measured: boolean;
  /** 1 until measured, then the clamped multiplier. */
  readonly multiplier: number;
  /** Whether any chunk at all could be timed. Ruling 64e. */
  readonly timeable: boolean;
  /** Which mark the timings came from, or null if none were had. */
  readonly source: TimingSource | null;
  readonly timedMs: number;
  readonly timedWords: number;
}

export interface LiveRateChunk {
  readonly elapsedMs: number;
  readonly source: TimingSource;
  readonly words: number;
}

export interface LiveRate {
  /** Fold one spoken chunk in, and report the state after it. */
  record(chunk: LiveRateChunk): LiveRateState;
  /** Note that a chunk could not be timed at all. Ruling 64e. */
  recordUntimed(): LiveRateState;
  state(): LiveRateState;
}

export function createLiveRate(targetWpm: number): LiveRate {
  if (!Number.isFinite(targetWpm) || targetWpm <= 0) {
    throw new RangeError("target words per minute must be positive");
  }
  let timedWords = 0;
  let timedMs = 0;
  let multiplier = 1;
  let measured = false;
  let baselineWpm: number | null = null;
  let source: TimingSource | null = null;
  let untimed = 0;

  const snapshot = (): LiveRateState => ({
    baselineWpm,
    measured,
    multiplier,
    // False only when nothing has been timed and something was tried.
    timeable: timedWords > 0 || untimed === 0,
    source,
    timedMs,
    timedWords,
  });

  return {
    state: snapshot,
    recordUntimed: () => {
      untimed += 1;
      return snapshot();
    },
    record: (chunk) => {
      if (chunk.words <= 0 || chunk.elapsedMs <= 0) return snapshot();
      timedWords += chunk.words;
      timedMs += chunk.elapsedMs;
      // The weaker mark wins the label: if any chunk had to fall back to the
      // speak call, the figure as a whole carries that caveat.
      source = source === "speak-call" ? source : chunk.source;
      if (!measured && timedWords >= MEASUREMENT_WORDS) {
        // `calculateCalibration` is written against a 60-word passage, so
        // the measured span is scaled to what 60 words would have taken.
        // Reusing it keeps one clamp and one definition of reachable.
        const msForSixtyWords = timedMs * (MEASUREMENT_WORDS / timedWords);
        const result = calculateCalibration(msForSixtyWords, targetWpm);
        baselineWpm = result.baselineWpm;
        multiplier = result.clampedMultiplier;
        measured = true;
      }
      return snapshot();
    },
  };
}

/** What the screen says about the rate. Ruling 64e. */
export function describeLiveRate(
  state: LiveRateState,
  targetWpm: number,
): string {
  if (!state.timeable) {
    return "This voice reports no timing, so delivery is running at the voice's own speed and the rate cannot be set.";
  }
  if (!state.measured) {
    return `Rate not yet measured. Delivering at the voice's own speed until ${String(MEASUREMENT_WORDS)} words have been timed.`;
  }
  const caveat =
    state.source === "speak-call"
      ? " Measured from the speak call, because this voice does not report when it starts, so it reads slightly slow."
      : "";
  /* v8 ignore next -- measured is only ever set with a baseline beside it */
  const baseline = state.baselineWpm ?? 0;
  return `${String(targetWpm)} wpm, from a measured ${String(Math.round(baseline))} wpm for this voice.${caveat}`;
}
